import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader, Panel, StatTile, DataTable, Chip, Button, Modal, FormRow, Select, Textarea, Switch, Banner, EmptyState, useToast, type Column } from '../ds/index'
import { useApi } from '../lib/useApi'
import { post, patch } from '../lib/api'
import { money, num, dateShort, timeShort, ago } from '../lib/format'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import ClientDocuments from '../components/ClientDocuments'

type BalanceRow = {
  id: string; name: string; status: string; auto_buy: boolean; auto_buy_cap_cents: number | null; category_name: string | null
  credit_cents: number; deposited_this_month_cents: number; minimum_met: boolean; logins: number; open_disputes: number
}

const toCents = (v: string) => {
  const t = v.replace(/[$,\s]/g, '')
  if (!t) return null
  return /^-?\d+(\.\d{1,2})?$/.test(t) ? Math.round(Number(t) * 100) : NaN
}

export default function Balances() {
  const { data, error } = useApi<{ pricing_mode: string; monthly_minimum_cents: number; clients: BalanceRow[] }>('/api/clients/balances')
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  const c = data.clients
  const total = c.reduce((t, r) => t + r.credit_cents, 0)
  const missing = c.filter((r) => r.status === 'active' && !r.minimum_met)
  const deposited = c.reduce((t, r) => t + r.deposited_this_month_cents, 0)

  const cols: Column<BalanceRow>[] = [
    { key: 'name', header: 'Client', cell: (r) => <><Link to={`/clients/${r.id}`}><strong>{r.name}</strong></Link><div className="muted">{r.category_name}{r.status !== 'active' ? `, ${r.status}` : ''}</div></>, sortBy: (r) => r.name },
    { key: 'credit', header: 'Lead credit', numeric: true, cell: (r) => <strong className={r.credit_cents <= 0 ? 'tone-warn' : undefined}>{money(r.credit_cents)}</strong>, sortBy: (r) => r.credit_cents },
    { key: 'month', header: 'Put in this month', numeric: true, cell: (r) => <>{money(r.deposited_this_month_cents)}{!r.minimum_met && r.status === 'active' && <div className="tone-warn nowrap">under the minimum</div>}</>, sortBy: (r) => r.deposited_this_month_cents },
    { key: 'auto', header: 'Auto-buy', cell: (r) => (!r.auto_buy ? <span className="muted">Off</span> : r.auto_buy_cap_cents == null ? <span>No limit</span> : <span>Up to {money(r.auto_buy_cap_cents)}</span>), sortBy: (r) => (!r.auto_buy ? -1 : r.auto_buy_cap_cents == null ? Number.MAX_SAFE_INTEGER : r.auto_buy_cap_cents) },
    { key: 'logins', header: 'Logins', numeric: true, cell: (r) => (r.logins ? num(r.logins) : <span className="muted">none</span>), sortBy: (r) => r.logins },
    { key: 'disputes', header: 'Disputes', cell: (r) => (r.open_disputes ? <Link to={`/clients/${r.id}#disputes`}><Chip tone="warn" dot>{r.open_disputes} open</Chip></Link> : null), sortBy: (r) => r.open_disputes },
  ]

  return (
    <>
      <PageHeader title="Balances" subtitle="Each client's prepaid lead credit, what they put in this month, and whether auto-buy keeps leads coming when it runs out." />
      {data.pricing_mode !== 'tiers' && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title="Balances are not in use">Leads are billed at each buyer's own price. Turn on tier pricing under Tiers and pricing.</Banner></div>}
      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile focal label="Lead credit held" value={money(total)} meta={`across ${c.length} clients`} />
        <StatTile label="Put in this month" value={money(deposited)} meta={`minimum is ${money(data.monthly_minimum_cents)} each`} />
        <StatTile label="Under the minimum" value={missing.length} meta={missing.length ? missing.slice(0, 3).map((m) => m.name).join(', ') : 'everyone is in'} />
        <StatTile label="Auto-buy on" value={c.filter((r) => r.auto_buy).length} meta="keep getting leads past their balance" />
      </div>
      <Panel className="panel-flush">
        <DataTable columns={cols} rows={c} rowKey={(r) => r.id} empty={<EmptyState title="No clients yet" body="Add buyers on the Buyers page." />} />
      </Panel>
    </>
  )
}

type Entry = {
  id: string; kind: 'deposit' | 'lead' | 'refund' | 'adjustment' | 'gift'; credit_cents: number; cash_cents: number; method: string | null
  reference: string | null; note: string | null; created_at: string; created_by_name: string | null; summary: string | null; tier_name: string | null
}
type Wallet = {
  client: { id: string; name: string; status: string; tiers_wanted: string[] | null; weekly_lead_limit: number | null; autopay: boolean; card: { brand: string | null; last4: string | null; exp: string | null } | null }
  summary: {
    credit_cents: number; cash_cents: number; pending_cents: number; pending_leads: number; overage_this_month_cents: number
    auto_buy: boolean; auto_buy_cap_cents: number | null; auto_buy_room_cents: number | null; available_cents: number | null
    deposited_this_month_cents: number; monthly_minimum_cents: number; minimum_met: boolean; discount_bps: number
  }
  entries: Entry[]
  tiers: { id: string; key: string; name: string; price_cents: number }[]
}
type Login = { id: string; name: string; email: string; active: boolean; has_password: boolean; last_seen_at: string | null }

const KIND: Record<Entry['kind'], string> = { deposit: 'Money in', lead: 'Lead', refund: 'Refund', adjustment: 'Correction', gift: 'Sign-up gift' }

/** One client's money: balance, money in, corrections, buying settings, their logins, and every ledger row. */
export function ClientWallet({ clientId, embedded = false }: { clientId?: string; embedded?: boolean } = {}) {
  const params = useParams()
  const id = clientId || params.id
  const w = useApi<Wallet>(`/api/clients/${id}/wallet`)
  const logins = useApi<{ logins: Login[] }>(`/api/clients/${id}/logins`)
  const [modal, setModal] = useState<'deposit' | 'adjust' | 'login' | null>(null)
  if (w.error) return <ErrorBlock error={w.error} />
  if (!w.data) return <LoadingBlock />
  const { client, summary: s, entries } = w.data
  const reload = () => { w.reload(); logins.reload() }

  return (
    <>
      {embedded ? (
        <div className="panel-header" style={{ marginBottom: 'var(--s-4)', flexWrap: 'wrap', gap: 'var(--s-3)', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 220px' }}><h2 className="panel-title">Balance and buying</h2><p className="muted" style={{ margin: 0 }}>{`Lead credit, money in, and how this client buys. ${client.card ? `Card on file: ${client.card.brand || 'card'} ending ${client.card.last4}, autopay ${client.autopay ? 'on' : 'off'}.` : 'No card on file yet.'}`}</p></div>
          <Button onClick={() => setModal('deposit')}>Record money in</Button>
        </div>
      ) : (
        <PageHeader title={client.name} subtitle={`Lead credit, money in, and how this client buys. ${client.card ? `Card on file: ${client.card.brand || 'card'} ending ${client.card.last4}, autopay ${client.autopay ? 'on' : 'off'}.` : 'No card on file yet.'}`}
          actions={<div className="row-tight"><Link className="btn btn-ghost" to="/clients">All clients</Link><Button onClick={() => setModal('deposit')}>Record money in</Button></div>} />
      )}
      {!s.minimum_met && <div style={{ marginBottom: 'var(--s-6)' }}><Banner tone="warn" title="Under this month's minimum">{money(s.deposited_this_month_cents)} of {money(s.monthly_minimum_cents)} put in so far.</Banner></div>}
      <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
        <StatTile focal label="Lead credit" value={money(s.credit_cents)} meta={`${money(s.cash_cents)} of money behind it`} />
        <StatTile label="On the way" value={money(s.pending_cents)} meta={`${s.pending_leads} lead${s.pending_leads === 1 ? '' : 's'} waiting on a rep's reply`} />
        <StatTile label="Auto-buy this month" value={s.auto_buy ? money(s.overage_this_month_cents) : 'Off'} meta={!s.auto_buy ? 'leads stop when credit runs out' : s.auto_buy_cap_cents == null ? 'no limit set' : `of ${money(s.auto_buy_cap_cents)}, ${money(s.auto_buy_room_cents ?? 0)} left`}
                  progress={s.auto_buy && s.auto_buy_cap_cents ? (s.overage_this_month_cents / s.auto_buy_cap_cents) * 100 : undefined} />
        <StatTile label="Can take on now" value={s.available_cents == null ? 'No limit' : money(Math.max(0, s.available_cents))} meta="credit plus auto-buy room, less what is on the way" />
      </div>

      <div className="wide-narrow">
        <Panel className="panel-flush">
          <div className="panel-header" style={{ padding: 'var(--s-5) var(--s-6) 0' }}>
            <h2 className="panel-title">Every movement</h2>
            <Button variant="ghost" onClick={() => setModal('adjust')}>Correct the balance</Button>
          </div>
          <Ledger entries={entries} />
        </Panel>
        <div className="stack">
          <BuyingPanel wallet={w.data} onSaved={reload} />
          <Panel>
            <div className="panel-header"><h2 className="panel-title">Client logins</h2><Button variant="secondary" onClick={() => setModal('login')}>Add login</Button></div>
            {logins.data && logins.data.logins.length === 0 && <p className="muted" style={{ margin: 0 }}>Nobody at {client.name} can sign in yet.</p>}
            {logins.data?.logins.map((l) => (
              <div key={l.id} className="row-tight" style={{ justifyContent: 'space-between', flexWrap: 'wrap', padding: 'var(--s-3) 0', borderTop: '1px solid var(--border)' }}>
                <div><strong>{l.name}</strong><div className="muted">{l.email}</div></div>
                <span className="muted nowrap">{!l.active ? 'turned off' : !l.has_password ? 'invited, not signed in' : l.last_seen_at ? `seen ${ago(l.last_seen_at)}` : 'never signed in'}</span>
              </div>
            ))}
          </Panel>
        </div>
      </div>

      <ClientProfilePanel clientId={client.id} />
      <div style={{ height: 'var(--s-6)' }} />
      <ClientDocuments clientId={client.id} clientName={client.name} />

      {modal === 'deposit' && <DepositModal clientId={client.id} discountBps={s.discount_bps} onClose={() => setModal(null)} onSaved={reload} />}
      {modal === 'adjust' && <AdjustModal clientId={client.id} onClose={() => setModal(null)} onSaved={reload} />}
      {modal === 'login' && <LoginModal clientId={client.id} clientName={client.name} onClose={() => setModal(null)} onSaved={reload} />}
    </>
  )
}

function Ledger({ entries }: { entries: Entry[] }) {
  if (!entries.length) return <div style={{ padding: 'var(--s-6)' }}><EmptyState title="No money has moved yet" body="Record money in to give this client lead credit." /></div>
  const cols: Column<Entry>[] = [
    { key: 'when', header: 'When', cell: (e) => <span className="nowrap">{dateShort(e.created_at)} {timeShort(e.created_at)}</span>, sortBy: (e) => e.created_at },
    { key: 'what', header: 'What', cell: (e) => (
      <>
        <strong>{KIND[e.kind]}</strong>{e.tier_name ? <span className="muted"> · {e.tier_name}</span> : null}
        <div className="muted">{e.kind === 'lead' || e.kind === 'refund' ? (e.summary || 'a lead') : [e.method, e.reference, e.note].filter(Boolean).join(' · ')}</div>
        {e.created_by_name && <div className="muted">by {e.created_by_name}</div>}
      </>
    ) },
    { key: 'credit', header: 'Credit', numeric: true, cell: (e) => <span className={e.credit_cents < 0 ? undefined : 'tone-pos'}>{e.credit_cents > 0 ? '+' : ''}{money(e.credit_cents)}</span>, sortBy: (e) => e.credit_cents },
    { key: 'cash', header: 'Money', numeric: true, cell: (e) => <span className="muted">{e.cash_cents > 0 ? '+' : ''}{money(e.cash_cents)}</span>, sortBy: (e) => e.cash_cents },
  ]
  return <DataTable columns={cols} rows={entries} rowKey={(e) => e.id} />
}

function BuyingPanel({ wallet, onSaved }: { wallet: Wallet; onSaved: () => void }) {
  const toast = useToast()
  const s = wallet.summary
  const [auto, setAuto] = useState(s.auto_buy)
  const [cap, setCap] = useState(s.auto_buy_cap_cents != null ? String(s.auto_buy_cap_cents / 100) : '')
  const [weekly, setWeekly] = useState(wallet.client.weekly_lead_limit ? String(wallet.client.weekly_lead_limit) : '')
  const [tiers, setTiers] = useState<string[]>(wallet.client.tiers_wanted || wallet.tiers.map((t) => t.key))
  const [busy, setBusy] = useState(false)
  const save = async () => {
    const c = toCents(cap)
    if (auto && (c === null || Number.isNaN(c) || c < 0)) return toast('Set a monthly limit for auto-buy', 'neg')
    if (!tiers.length) return toast('Choose at least one tier', 'neg')
    setBusy(true)
    try {
      await patch(`/api/clients/${wallet.client.id}/buying`, {
        auto_buy: auto, auto_buy_cap_cents: Number.isNaN(c) ? null : c,
        tiers_wanted: tiers.length === wallet.tiers.length ? null : tiers,
        weekly_lead_limit: weekly ? Number(weekly) : null,
      })
      toast('Saved', 'pos'); onSaved()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Panel>
      <div className="panel-header"><h2 className="panel-title">How they buy</h2></div>
      <div className="stack-tight">
        <Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} label="Auto-buy when their credit runs out" />
        {auto && (
          <FormRow label="Monthly limit, dollars" htmlFor="w-cap" help="Full price, billed at the end of the month">
            <input id="w-cap" className="field" inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value)} />
          </FormRow>
        )}
        <FormRow label="Tiers they take" htmlFor="w-tiers">
          <div id="w-tiers" className="row-tight" style={{ flexWrap: 'wrap' }}>
            {wallet.tiers.map((t) => (
              <label key={t.key} className="row-tight" style={{ alignItems: 'center' }}>
                <input type="checkbox" checked={tiers.includes(t.key)} onChange={(e) => setTiers((xs) => (e.target.checked ? [...xs, t.key] : xs.filter((k) => k !== t.key)))} />
                <span>{t.name} <span className="muted">{money(t.price_cents)}</span></span>
              </label>
            ))}
          </div>
        </FormRow>
        <FormRow label="Most leads a week (optional)" htmlFor="w-weekly">
          <input id="w-weekly" className="field" inputMode="numeric" value={weekly} onChange={(e) => setWeekly(e.target.value.replace(/\D/g, ''))} />
        </FormRow>
        <div><Button variant="secondary" onClick={save} disabled={busy}>Save</Button></div>
      </div>
    </Panel>
  )
}

function DepositModal({ clientId, discountBps, onClose, onSaved }: { clientId: string; discountBps: number; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState('card')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const c = toCents(amount)
  const credit = c && !Number.isNaN(c) ? Math.round(c / (1 - discountBps / 10000)) : null
  const save = async () => {
    if (!c || Number.isNaN(c) || c <= 0) return toast('Enter the amount received', 'neg')
    setBusy(true)
    try {
      await post(`/api/clients/${clientId}/deposits`, { amount_cents: c, method, reference: reference || undefined, note: note || undefined })
      toast(`${money(c)} recorded`, 'pos'); onSaved(); onClose()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Modal title="Record money in" onClose={onClose}
           footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>Record {c && !Number.isNaN(c) ? money(c) : ''}</Button></>}>
      <div className="stack-tight">
        <p style={{ margin: 0 }}>Only record money you have received. It becomes lead credit at {discountBps / 100}% off.</p>
        <FormRow label="Amount received, dollars" htmlFor="d-amt" help={credit ? `${money(credit)} of lead credit` : undefined}>
          <input id="d-amt" className="field" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </FormRow>
        <FormRow label="Paid by" htmlFor="d-method">
          <Select id="d-method" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="card">Card</option><option value="ach">Bank transfer</option><option value="check">Check</option><option value="cash">Cash</option><option value="other">Other</option>
          </Select>
        </FormRow>
        <FormRow label="Reference (optional)" htmlFor="d-ref"><input id="d-ref" className="field" value={reference} maxLength={100} onChange={(e) => setReference(e.target.value)} /></FormRow>
        <FormRow label="Note (optional)" htmlFor="d-note"><input id="d-note" className="field" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} /></FormRow>
      </div>
    </Modal>
  )
}

function AdjustModal({ clientId, onClose, onSaved }: { clientId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [credit, setCredit] = useState('')
  const [cash, setCash] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    const cr = toCents(credit) ?? 0
    const ca = toCents(cash) ?? 0
    if (Number.isNaN(cr) || Number.isNaN(ca)) return toast('Enter amounts in dollars, with a minus sign to take away', 'neg')
    if (!note.trim()) return toast('Say why in the note', 'neg')
    setBusy(true)
    try {
      await post(`/api/clients/${clientId}/adjustments`, { credit_cents: cr, cash_cents: ca, note })
      toast('Corrected', 'pos'); onSaved(); onClose()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  return (
    <Modal title="Correct the balance" onClose={onClose}
           footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>Save correction</Button></>}>
      <div className="stack-tight">
        <p style={{ margin: 0 }}>For mistakes and goodwill credit. Use a minus sign to take away. The note is kept with the change.</p>
        <FormRow label="Lead credit, dollars" htmlFor="a-credit" help="What the client's balance shows"><input id="a-credit" className="field" inputMode="decimal" value={credit} onChange={(e) => setCredit(e.target.value)} /></FormRow>
        <FormRow label="Money behind it, dollars" htmlFor="a-cash" help="Leave blank for free credit, like a goodwill gesture"><input id="a-cash" className="field" inputMode="decimal" value={cash} onChange={(e) => setCash(e.target.value)} /></FormRow>
        <FormRow label="Why" htmlFor="a-note"><Textarea id="a-note" rows={2} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} /></FormRow>
      </div>
    </Modal>
  )
}

function LoginModal({ clientId, clientName, onClose, onSaved }: { clientId: string; clientName: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [invite, setInvite] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try {
      const r = await post<{ invite: { url: string } }>(`/api/clients/${clientId}/logins`, { name, email })
      setInvite(r.invite.url); onSaved()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const copy = async () => {
    try { await navigator.clipboard.writeText(invite || ''); toast('Link copied', 'pos') } catch { toast('Select the link and copy it', 'neg') }
  }
  return (
    <Modal title={`Add a login for ${clientName}`} onClose={onClose}
           footer={invite ? <Button onClick={onClose}>Done</Button> : <><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy || !name || !email}>Add login</Button></>}>
      {invite ? (
        <div className="stack-tight">
          <p style={{ margin: 0 }}>Send this link to {name}. It works once, for 7 days, and lets them set a password. They will see only {clientName}'s leads, balance and statements.</p>
          <input className="field" readOnly value={invite} onFocus={(e) => e.target.select()} aria-label="Invite link" />
          <div><Button variant="secondary" onClick={copy}>Copy link</Button></div>
        </div>
      ) : (
        <div className="stack-tight">
          <FormRow label="Name" htmlFor="l-name"><input id="l-name" className="field" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></FormRow>
          <FormRow label="Email" htmlFor="l-email"><input id="l-email" className="field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></FormRow>
        </div>
      )}
    </Modal>
  )
}

type IntakeView = {
  sections: { key: string; title: string; questions: { key: string; label: string }[]; lines: { key: string; label: string; value: string }[] }[]
  answers: Record<string, string | number | string[]>; updated_at: string | null
  completion: { percent: number; complete: boolean; sections: { key: string; complete: boolean; answered: number }[] }
}

/** What the client told us in their business profile. The AI matches leads to it; reps see it before recommending. */
function ClientProfilePanel({ clientId }: { clientId: string }) {
  const { data, error } = useApi<IntakeView>(`/api/clients/${clientId}/intake`)
  if (error) return <ErrorBlock error={error} />
  if (!data) return null
  const c = data.completion
  return (
    <Panel>
      <div className="panel-header">
        <h2 className="panel-title">Business profile</h2>
        <span className="row-tight" style={{ alignItems: 'center' }}>
          <span className="muted">{data.updated_at ? `updated ${dateShort(data.updated_at)}` : 'not started'}</span>
          <Chip tone={c.complete ? 'pos' : 'warn'} dot>{c.complete ? 'Complete' : `${c.percent}% done`}</Chip>
        </span>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>The client fills this in from their portal. The AI reads it when it decides who suits a lead.</p>
      <div className="profile-admin">
        {data.sections.map((sec) => {
          // Labelled and formatted as the client saw them: monthly amounts read as monthly, dollars as dollars.
          return (
            <section key={sec.key}>
              <h3>{sec.title}</h3>
              {sec.lines.length === 0 ? <p className="muted">Not answered yet.</p> : (
                <dl>
                  {sec.lines.map((l) => <div key={l.key}><dt>{l.label}</dt><dd>{l.value}</dd></div>)}
                </dl>
              )}
            </section>
          )
        })}
      </div>
    </Panel>
  )
}
